/**
 * Centralised locale-aware formatting helpers (issue #337).
 *
 * All user-facing date / time / number / currency formatting MUST go through
 * this module so that:
 *   1. The display locale follows the active i18n language (not a hardcoded
 *      'es-ES' / 'en-GB' literal sprinkled across the codebase).
 *   2. Adding a third language is a one-line change per map below.
 *   3. Dates render in the business timezone (Atlantic/Canary) by default.
 */
import { es, enUS } from 'date-fns/locale';
import type { Locale } from 'date-fns';
import i18n from '@/i18n';
import { KNOWN_LOCALES, FALLBACK_LOCALE_CODE } from '@/lib/i18n/localeRegistry';

/** Business timezone for the Canary Islands ISP. */
export const CANARY_TIMEZONE = 'Atlantic/Canary';

/**
 * date-fns locale objects keyed by their `dateFnsKey` (from the locale
 * registry). Adding a new language = one import + one entry here, then a
 * registry entry — see `lib/i18n/localeRegistry.ts`.
 */
const DATE_FNS_OBJECTS: Record<string, Locale> = {
  enUS,
  es,
};

/** i18n language → BCP-47 Intl locale tag, derived from the locale registry. */
const INTL_LOCALE_MAP: Record<string, string> = Object.fromEntries(
  KNOWN_LOCALES.map((l) => [l.code, l.intlLocale]),
);

/** i18n language → date-fns locale object, derived from the locale registry. */
const DATE_FNS_LOCALE_MAP: Record<string, Locale> = Object.fromEntries(
  KNOWN_LOCALES.map((l) => [l.code, DATE_FNS_OBJECTS[l.dateFnsKey] ?? enUS]),
);

const FALLBACK_LANGUAGE = FALLBACK_LOCALE_CODE;

/** Normalise an i18n language code ('es', 'es-ES', …) to a supported base language. */
function normalizeLanguage(lang?: string): string {
  const base = (lang || i18n.language || FALLBACK_LANGUAGE).split('-')[0].toLowerCase();
  return base in INTL_LOCALE_MAP ? base : FALLBACK_LANGUAGE;
}

/**
 * Get the Intl locale tag for the current (or given) i18n language.
 * Maps i18n language codes to full BCP-47 locale codes (en → en-GB, es → es-ES).
 */
export function getIntlLocale(lang?: string): string {
  return INTL_LOCALE_MAP[normalizeLanguage(lang)];
}

/** Get the date-fns locale object for the current (or given) i18n language. */
export function getDateFnsLocale(lang?: string): Locale {
  return DATE_FNS_LOCALE_MAP[normalizeLanguage(lang)];
}

type DateInput = string | number | Date | null | undefined;

function toDate(value: Exclude<DateInput, null | undefined>): Date {
  return value instanceof Date ? value : new Date(value);
}

/**
 * Format a date with locale-aware formatting in the Atlantic/Canary timezone.
 * Default style: "9 Jun 2026" (en) / "9 jun 2026" (es).
 *
 * Pass `options` to fully override the date parts (timezone default is kept):
 *   formatDate(d, { month: 'long', year: 'numeric' }) → "June 2026"
 */
export function formatDate(value: DateInput, options?: Intl.DateTimeFormatOptions, lang?: string): string {
  if (value === null || value === undefined || value === '') return '-';
  return toDate(value).toLocaleDateString(getIntlLocale(lang), {
    timeZone: CANARY_TIMEZONE,
    ...(options ?? { year: 'numeric', month: 'short', day: 'numeric' }),
  });
}

/**
 * Format date and time with locale-aware formatting in the Atlantic/Canary timezone.
 * Default style: "9 June 2026, 14:30" (en).
 */
export function formatDateTime(value: DateInput, options?: Intl.DateTimeFormatOptions, lang?: string): string {
  if (value === null || value === undefined || value === '') return '-';
  return toDate(value).toLocaleString(getIntlLocale(lang), {
    timeZone: CANARY_TIMEZONE,
    ...(options ?? {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }),
  });
}

/**
 * Format the time-of-day portion of a date in the Atlantic/Canary timezone.
 * Default style: "14:30".
 */
export function formatTime(value: DateInput, options?: Intl.DateTimeFormatOptions, lang?: string): string {
  if (value === null || value === undefined || value === '') return '-';
  return toDate(value).toLocaleTimeString(getIntlLocale(lang), {
    timeZone: CANARY_TIMEZONE,
    ...(options ?? { hour: '2-digit', minute: '2-digit' }),
  });
}

/**
 * The ISP's base business currency symbol, for the rare cases where the UI
 * needs a static currency adornment (e.g. a form-input suffix showing what
 * unit the field expects) rather than a fully-formatted amount. Centralising
 * this constant means no literal '€' needs to be duplicated across source
 * files (CONS-003 — the literal is banned by an ESLint `no-restricted-syntax`
 * guard everywhere except this module). Prefer `formatCurrency()`/`<Money>`
 * for anything that renders an actual amount.
 */
export const CURRENCY_SYMBOL = '€';

/**
 * Format currency with locale-aware formatting.
 * Defaults to EUR (Canary Islands).
 */
export function formatCurrency(
  amount: number,
  options?: Intl.NumberFormatOptions,
  lang?: string,
): string {
  return new Intl.NumberFormat(getIntlLocale(lang), {
    style: 'currency',
    currency: 'EUR',
    ...options,
  }).format(amount);
}

/** Format a plain number with locale-aware grouping/decimal separators. */
export function formatNumber(value: number, options?: Intl.NumberFormatOptions, lang?: string): string {
  return new Intl.NumberFormat(getIntlLocale(lang), options).format(value);
}

/**
 * Format an EXACT decimal amount — already split into sign/integer/fraction
 * digit strings so the real value never passes through `Number()` — as
 * localized currency. This is the money-specific sibling of
 * `formatCurrency()` for callers holding an exact decimal string (e.g. M4-F
 * reconciliation's `MoneyString` contract, which forbids binary-float
 * financial semantics anywhere in the pipeline —
 * `lib/reconciliation/money.ts` does the string parsing that produces
 * `parts` and is this function's only caller today).
 *
 * `Intl.NumberFormat.formatToParts()` is used ONLY to read this
 * locale+currency's formatting metadata (symbol, spacing, grouping/decimal
 * separators, minus-sign placement) via a fixed literal reference value
 * (`±1234.56`, chosen only for its sign) — the real amount's digits are
 * spliced into that template as plain strings and never converted to a
 * `number`.
 */
export function formatExactMoneyParts(
  parts: { negative: boolean; integerDigits: string; fractionDigits: string },
  currency: string,
  lang?: string,
): string {
  const referenceValue = parts.negative ? -1234.56 : 1234.56;
  const referenceParts = new Intl.NumberFormat(getIntlLocale(lang), {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).formatToParts(referenceValue);

  const groupSeparator = referenceParts.find((part) => part.type === 'group')?.value ?? '';
  const decimalSeparator = referenceParts.find((part) => part.type === 'decimal')?.value ?? '.';
  const groupedIntegerDigits = groupSeparator
    ? parts.integerDigits.replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator)
    : parts.integerDigits;

  let integerEmitted = false;
  let result = '';
  for (const part of referenceParts) {
    if (part.type === 'integer' || part.type === 'group') {
      if (!integerEmitted) {
        result += groupedIntegerDigits;
        integerEmitted = true;
      }
      continue;
    }
    if (part.type === 'decimal') {
      result += decimalSeparator;
      continue;
    }
    if (part.type === 'fraction') {
      result += parts.fractionDigits;
      continue;
    }
    // currency symbol, literal spacing, minus sign, etc. — verbatim from the locale.
    result += part.value;
  }
  return result;
}

/**
 * Format a percentage. `value` is the percentage as commonly stored in this
 * codebase (0–100 scale, e.g. 7 → "7%"), NOT an Intl-style fraction.
 */
export function formatPercent(value: number, options?: Intl.NumberFormatOptions, lang?: string): string {
  return new Intl.NumberFormat(getIntlLocale(lang), {
    style: 'percent',
    maximumFractionDigits: 1,
    ...options,
  }).format(value / 100);
}

/**
 * Format a date as relative time (e.g., "2 hours ago", "hace 2 horas").
 * Falls back to a short formatted date for older dates (> 7 days).
 *
 * Uses `Intl.RelativeTimeFormat` (same Intl family as the rest of this file)
 * so the wording is genuinely localized instead of hardcoded English —
 * NEW-002: this used to emit literal 'just now' / 'Nm ago' regardless of the
 * active i18n language. `numeric: 'auto'` also picks up idiomatic forms the
 * old code couldn't ("now", "yesterday"/"ayer", "the day before
 * yesterday"/"anteayer") where the locale has one.
 */
export function formatRelativeTime(value: DateInput): string {
  if (value === null || value === undefined || value === '') return '-';
  const date = toDate(value);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  const rtf = new Intl.RelativeTimeFormat(getIntlLocale(), { numeric: 'auto' });

  if (diffSeconds < 60) return rtf.format(0, 'second');
  if (diffMinutes < 60) return rtf.format(-diffMinutes, 'minute');
  if (diffHours < 24) return rtf.format(-diffHours, 'hour');
  if (diffDays < 7) return rtf.format(-diffDays, 'day');
  // Fall back to short date for older entries
  return formatDate(date, { month: 'short', day: 'numeric' });
}

/**
 * Format a local Date object as a YYYY-MM-DD string using the browser's local
 * calendar fields (getFullYear / getMonth / getDate).
 *
 * NEVER use `date.toISOString().split('T')[0]` for date-range inputs that are
 * sent to the API — that method converts to UTC first, which shifts the date
 * backwards by one day for Atlantic/Canary (UTC+1 in summer).  BUG-E38.
 *
 * Also works as a drop-in for "today's date" in form defaults:
 *   formatLocalDate(new Date())  →  "2026-06-09"
 */
export function formatLocalDate(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Get the YYYY-MM-DD calendar date of a moment AS SEEN IN Atlantic/Canary,
 * regardless of the browser's timezone. (The 'sv-SE' locale renders dates in
 * ISO format — the standard trick for timezone-shifted ISO dates.)
 *
 * Use this — not formatLocalDate() — when comparing against business-day
 * boundaries (e.g. "is this task due today in the Canaries?").
 */
export function toCanaryDateISO(date: Date = new Date()): string {
  return date.toLocaleDateString('sv-SE', { timeZone: CANARY_TIMEZONE });
}

/** A Date whose local calendar fields mirror the current wall-clock time in Atlantic/Canary. */
export function nowInCanary(): Date {
  return new Date(new Date().toLocaleString('en-US', { timeZone: CANARY_TIMEZONE }));
}
